import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Group, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPencil, IconTrash } from "@tabler/icons-react";

import { openDeleteModal } from "@components/DeleteModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useDeleteMedication } from "@hooks/mutations/prescription/useDeleteMedication";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Medication } from "@customTypes/prescription";

import { EditModal } from "../EditModal";


interface TableControlsProps {
  medication: Partial<Medication>;
  cId: string;
}

export const TableControls = ({ medication, cId }: TableControlsProps) => {
  const { t } = useTranslation();
  const { patientId } = useParams();

  const [editModalOpened, { open: editModalOpen, close: editModalClose }] =
    useDisclosure(false);
  const [medicationState, setMedicationState] =
    useState<Partial<Medication> | null>(null);

  const { mutate: deleteMedication, isLoading: isDeleteMedicationLoading } =
    useDeleteMedication();
  const handleErrors = useQueryErrorHandling();

  useEffect(() => {
    // console.log(row.original);
    if (medication) {
      setMedicationState(medication);
    }
  }, [medication]);

  return (
    <>
      <EditModal
        cId={cId}
        opened={editModalOpened}
        close={editModalClose}
        medication={medication}
      />

      <Group className="table-controls" justify="end">
        <Menu withArrow position="bottom-end" withinPortal>
          <Menu.Target>
            <PermissionGate permission="medications.delete"><Button
              variant="outline"
              size="compact-sm"
              loading={isDeleteMedicationLoading}>
              {t("action")}
            </Button></PermissionGate>
          </Menu.Target>
          <Menu.Dropdown>
            <PermissionGate permission="medications.edit"><Menu.Item
              leftSection={<IconPencil size={16} stroke={1.5} />}
              onClick={() => {
                editModalOpen();
              }}>
              {t("edit-medication")}
            </Menu.Item></PermissionGate>
            <PermissionGate permission="medications.delete"><Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() =>
                openDeleteModal({
                  onConfirm: () => {
                    const id = showLoadingNotification({});
                    deleteMedication(
                      {
                        medicationId: medicationState?.id as string,
                        prescriptionId:
                          medicationState?.prescriptionId as string,
                        patientId: patientId as string,
                      },
                      {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "delete-medication-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      }
                    );
                  },
                })
              }>
              {t("delete-medication")}
            </Menu.Item></PermissionGate>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <LastUpdatedTimestamp
        updatedDate={medication?.updatedAt as Date}
        className="last-updated"
      />
    </>
  );
};
